const en = {
  nav: {
    product: "Product",
    howItWorks: "How It Works",
    integrations: "Integrations",
    pricing: "Pricing",
    enterprise: "Enterprise",
    faq: "FAQ",
    signIn: "Sign in",
    getStarted: "Start Free — $49 in Credits",
  },
  hero: {
    badge: "Salesforce-native · Built by DulipTech",
    line1: "Ship AI agents",
    line2: "inside Salesforce.",
    line3: "Just one step ahead.",
    sub: "Gyeno is the Salesforce-native platform to build, test, and deploy AI agents directly inside the org your team already runs. No new stack. No subscription.",
    ctaPrimary: "Start Free — Just $49 in Credits",
    ctaSecondary: "Talk to Sales",
    thinking: "Agent thinking…",
    caption: "Gyeno Studio — Design, test, and ship AI agents on Salesforce.",
  },
  trust: {
    builtFor: "Built for Salesforce",
    native: "Native to your org",
    byDulip: "Engineered by DulipTech — Salesforce consulting & AI products",
    regions: "Available in US · UK · Middle East · APAC",
  },
  manifesto: {
    kicker: "The Gyeno Principle",
    ch1t: "Your Salesforce is the platform.",
    ch1b: "Agents shouldn't live in a separate app you have to wire up. Gyeno binds directly to your org's own Apex and Flows and runs inside Salesforce — it works where your data and your team already are.",
    ch2t: "Build free. Pay for usage.",
    ch2b: "Agent building is unlimited and free — forever. There is no subscription. You buy AI Credits from $49 and only spend them when your agents actually run.",
    ch3t: "Trust is a feature.",
    ch3b: "Action Runs, Policy Violations, Error Logs, Health — every agent decision is observable and governed inside your org's own permission model. Enterprise control isn't an add-on.",
  },
  how: {
    kicker: "How It Works",
    title: "From blank canvas to live agent in three moves.",
    s1t: "Start in the Studio",
    s1b: "Sign up and get predefined agents ready to use on day one — or start a new agent from a blank canvas in Gyeno Studio, right inside Salesforce.",
    s2t: "Assemble real actions, pick your LLM",
    s2b: "Drag staged workflows together from your org's own Apex and Flow actions — GetPipeline, FindStalledDeals, LogTask — no code. Choose Claude, GPT, or Gemini per agent.",
    s3t: "Test live, then publish",
    s3b: "Chat with your agent in the live Test Console before anyone else does. Publish version-controlled releases with notes — safe to iterate, easy to roll back.",
    cta: "See the full walkthrough",
  },
  features: {
    kicker: "Why Teams Pick Gyeno",
    title: "Everything you need. Nothing you have to babysit.",
    f1t: "Unlimited free Agent Builder",
    f1b: "Build as many AI agents as you need — agent creation itself is always free.",
    f2t: "14-day premium support",
    f2b: "Every new account gets two weeks of hands-on implementation help for your specific use case. Real engineers, not generic docs.",
    f3t: "Choice of leading LLMs",
    f3b: "Bring the model you trust: Claude, OpenAI GPT, and Google Gemini — switch per agent, no lock-in.",
    f4t: "Simple, low-cost entry",
    f4b: "No subscription. Start with a one-time minimum $49 AI Credit purchase — pay only for what your agents actually use.",
    f5t: "Predefined, ready-to-use agents",
    f5b: "Launch with pre-built agents on day one — plug-and-play, no cold start.",
    f6t: "Bring-Your-Own-Key (Enterprise)",
    f6b: "Connect your own enterprise LLM provider keys and contracts for full cost and data control.",
  },
  channels: {
    kicker: "Deploy Anywhere",
    title: "One agent. Every channel your customers use.",
    whatsapp: "Meta Cloud API, direct — receive and reply on WhatsApp.",
    instagram: "Meet customers in Instagram DMs.",
    teams: "Bring agents into Microsoft Teams.",
    slack: "Answer where your team works — Slack.",
    web: "Embeddable widget for any custom web app.",
  },
  pricingTeaser: {
    title: "No subscription. Just pay for what you use.",
    sub: "Agent building is free and unlimited. Activate with a one-time $49 minimum credit purchase, and credits are only consumed when agents run.",
    cta: "See pricing",
  },
  proof: {
    kicker: "Trusted by Salesforce teams",
    placeholder: "Customer logo",
  },
  entCallout: {
    title: "Enterprise-scale? Bring your own LLM key.",
    sub: "Connect your organization's own Claude, OpenAI, or Gemini contracts for full cost and data control — with custom implementation from the DulipTech team.",
    cta: "Contact us",
  },
  footer: {
    tagline: "Just One Step Ahead.",
    product: "Product",
    company: "Company",
    legal: "Legal",
    privacy: "Privacy Policy",
    terms: "Terms & Conditions",
    contact: "Contact",
    signup: "Sign Up",
    regions: "Available in the US, UK, Middle East, and APAC.",
    rights: "© 2026 DulipTech. All rights reserved.",
    builtBy: "Gyeno is built by DulipTech.",
  },
  cookie: {
    text: "We use cookies to run this site and, with your consent, to measure how it's used. See our",
    accept: "Accept all",
    reject: "Essentials only",
  },
  auth: {
    signupTitle: "Create your business account",
    signupSub: "Free predefined agents are waiting. Activate with $49 in credits whenever you're ready.",
    fullName: "Full name",
    email: "Business email",
    password: "Password",
    company: "Company name",
    country: "Country / Region",
    sfOrg: "Salesforce org type (optional)",
    createAccount: "Create account",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    loginTitle: "Welcome back",
    loginSub: "Sign in to your Gyeno account.",
    noAccount: "New to Gyeno?",
    signUp: "Sign up",
  },
  pricingPage: {
    kicker: "Pricing",
    title: "No subscriptions. Just AI Credits.",
    sub: "Everything about building is free. You only pay for what your agents use — starting with a one-time $49 minimum credit purchase.",
    freeTitle: "Agent building",
    freeDesc: "Unlimited agents, unlimited edits, the full Studio — free forever.",
    freeTag: "Free",
    predefTitle: "Predefined agents",
    predefDesc: "Pre-built agents ready to use from day one. No cold start.",
    predefTag: "Free",
    creditsTitle: "AI Credits",
    creditsDesc: "One-time $49 minimum purchase activates your account. Credits are consumed by actual agent usage — LLM calls and actions.",
    creditsTag: "From $49",
    supportNote: "Every new account includes 14 days of premium implementation support.",
    tableTitle: "Credit consumption rates",
    tableNote: "Final credit-to-usage rates are being finalized and will be published here.",
    tableCol1: "Operation",
    tableCol2: "Credits",
    row1: "LLM call (standard model)",
    row2: "LLM call (premium model)",
    row3: "Salesforce action execution",
    row4: "Channel message (WhatsApp / Teams / Slack)",
    tbd: "TBD",
    calcTitle: "Pick your starting credits",
    calcSub: "Minimum $49 — top up any time.",
    calcCredits: "credits",
    cta: "Sign Up & Buy Credits",
    entTitle: "Need your own LLM keys or a custom contract?",
    entSub: "Enterprise BYOK, volume pricing, and custom implementation are handled directly by our team.",
    entCta: "Contact Us",
    faqTitle: "Billing questions",
    bq1: "Do credits expire?",
    ba1: "The credit expiry policy is being finalized. Ask us via the contact form and we'll confirm for your region before you buy.",
    bq2: "Can I get a refund on unused credits?",
    ba2: "The refund policy is being finalized. Contact us for the current terms before purchasing.",
    bq3: "How do I top up?",
    ba3: "Buy additional credits any time from your account — no minimum after the first $49 activation purchase.",
    needsInput: "Policy pending business confirmation",
  },
  contactPage: {
    kicker: "Enterprise",
    title: "Let's scope your Salesforce AI rollout.",
    sub: "For large orgs that want Bring-Your-Own-Key, custom contracts, or hands-on implementation from the DulipTech team.",
    responseTime: "Our team responds within 1 business day.",
    fullName: "Full name",
    email: "Business email",
    company: "Company",
    jobTitle: "Job title",
    country: "Country / Region",
    companySize: "Company size",
    sfEdition: "Salesforce edition (optional)",
    useCase: "Tell us about your use case",
    contactMethod: "Preferred contact method",
    send: "Send inquiry",
    success: "Your email is ready to send — we'll reply within 1 business day.",
    demoTitle: "Prefer a live demo?",
    demoSub: "Our calendar is being set up — meanwhile, reach us via the contact form above.",
    demoKicker: "Book a demo",
    demoLead: "Pick a slot that suits you — a DulipTech engineer will walk you through Gyeno Studio live in your context.",
    demoScroll: "Schedule a 30-minute walkthrough with a DulipTech engineer.",
    demoCta: "Book a demo",
    byokTitle: "Bring-Your-Own-Key",
    byokDesc: "Connect your enterprise Claude, OpenAI, or Gemini contracts. Your keys, your data agreements, your cost control.",
    implTitle: "Custom implementation",
    implDesc: "DulipTech engineers design, build, and govern your agent fleet with you — from first use case to org-wide rollout.",
    secTitle: "Security & governance",
    secDesc: "Agents run inside your org's permission model with Action Runs, Policy Violations, and Error Logs fully visible.",
  },
  faqPage: {
    kicker: "FAQ",
    title: "Answers, straight.",
    sub: "Everything Salesforce teams ask before they start.",
    stillQ: "Still have a question?",
    stillCta: "Contact us",
  },
  legal: {
    reviewFlag: "Draft for review — legal counsel should review this copy before public launch.",
    inputFlag: "Sections marked [NEEDS BUSINESS INPUT] require final policy decisions before launch.",
  },
  common: {
    selectPlaceholder: "Select…",
  },
};

export default en;
